import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { splitNote } from "@/utils/numberlike";
import {
  type AddressValue,
  type ScalarFact,
  type ArrayFact,
  type FactDefinition,
  type FactValue as FactValueType,
  type ScalarKind,
} from "../types";
import {
  isAddressUnplaceable,
  type MappedAddress,
  UNPLACEABLE_ADDRESS_TOOLTIP,
} from "../utils/address";
import AddressMap from "./AddressMap";
import { truncateText } from "../../utils";
import { formatNumberDisplay } from "./numberDisplay";
import InlineEntityCard from "../Cards/InlineEntityCard";
import { humanizeEnumKey } from "../../Data/humanizeEnumKey";
import { type EnumToneStyles } from "../../design/enumTone";
import {
  factValueTone,
  isValueHuedVocabulary,
} from "../../design/factValueTone";
import { earnsChip } from "../../design/chipCriteria";
import {
  hierarchicalValueLabel,
  PATH_SEPARATOR,
} from "../../design/hierarchicalValueLabel";
import { formatDateScalar } from "./dateScalar";
import UrlLink from "./UrlLink";

/** Compute relative luminance per sRGB (IEC-4WD) and return style colors. */
export const getEnumColorClasses = (hexColor: string) => {
  const hex = hexColor.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;

  // Convert to HSL
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }

  // Helper: HSL to hex
  const hslToHex = (h: number, s: number, l: number) => {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    let rr, gg, bb;
    if (s === 0) {
      rr = gg = bb = l;
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      rr = hue2rgb(p, q, h + 1 / 3);
      gg = hue2rgb(p, q, h);
      bb = hue2rgb(p, q, h - 1 / 3);
    }
    const toHex = (c: number) =>
      Math.round(c * 255)
        .toString(16)
        .padStart(2, "0");
    return `#${toHex(rr)}${toHex(gg)}${toHex(bb)}`;
  };

  // If the input color is already a light/pastel background (L > 0.75),
  // derive border at ~70% lightness and text at ~20% lightness
  if (l > 0.75) {
    return {
      backgroundColor: hexColor,
      borderColor: hslToHex(h, Math.min(s * 1.2, 1), 0.7),
      textColor: hslToHex(h, Math.min(s * 1.5, 1), 0.22),
    };
  }

  // For vivid/medium colors (legacy), lighten for background, darken for text
  return {
    backgroundColor: hslToHex(h, Math.min(s * 0.6, 1), 0.88),
    borderColor: hslToHex(h, Math.min(s * 0.7, 1), 0.72),
    textColor: hslToHex(h, Math.min(s * 1.2, 1), 0.25),
  };
};
import {
  AlertTriangle,
  ChevronRight,
  Hash,
  Calendar,
  DollarSign,
  Type,
  CheckCircle2,
  XCircle,
  List,
  MapPin,
  Sparkles,
} from "lucide-react";

// Auto-link URLs and emails in text
const URL_EMAIL_RE =
  /(https?:\/\/[^\s<>]+|[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,})/g;

const Linkify = ({ text }: { text: string }) => {
  const parts = text.split(URL_EMAIL_RE);
  if (parts.length === 1) return <>{text}</>;
  return (
    <>
      {parts.map((part, i) => {
        if (URL_EMAIL_RE.test(part)) {
          // Reset lastIndex since we're reusing the regex
          URL_EMAIL_RE.lastIndex = 0;
          const isEmail = !part.startsWith("http");
          // A URL shows whole when it fits its column and otherwise the way
          // Slack shortens a pasted link (#12010); UrlLink owns the fit.
          if (!isEmail) return <UrlLink key={i} href={part} />;
          return (
            <a
              key={i}
              href={`mailto:${part}`}
              className="text-blue-600 hover:text-blue-800 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {part}
            </a>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
};

// Simple tooltip component
const Tooltip = ({
  children,
  content,
}: {
  children: React.ReactNode;
  content: string;
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVisible && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      // Position tooltip above the element (8px above)
      setPosition({
        top: rect.top - 8,
        left: rect.left,
      });
    }
  }, [isVisible]);

  return (
    <>
      <div
        ref={wrapperRef}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        // max-w-full: shrink-to-fit sizing made this wrapper as wide as its
        // content, so the truncating span inside it never met its limit and
        // the cell's overflow-hidden clipped glyphs raw, mid-word and with no
        // ellipsis (#11426 keeper ruling C5).
        className="inline-block max-w-full"
      >
        {children}
      </div>
      {isVisible &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: `${position.top}px`,
              left: `${position.left}px`,
              transform: "translateY(-100%)",
              zIndex: 9999,
              backgroundColor: "#ffffff",
              color: "#111827",
              padding: "0.5rem",
              borderRadius: "0.25rem",
              fontSize: "0.875rem",
              maxWidth: "400px",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              boxShadow:
                "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              border: "1px solid #e5e7eb",
            }}
            onMouseEnter={() => setIsVisible(true)}
            onMouseLeave={() => setIsVisible(false)}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
};

// A scalar member of an array fact, as the washed chip the grid draws
// (#11426, round 8 — one renderer for one field on every surface). Null for
// non-scalar members (entities, documents, composites), which keep their
// own grammar. A hierarchical value renders its full stored path here: this
// chip feeds the overview rail and the Facts panel, where the whole path is
// the point (the leaf-only form is the table's, #12052). The tone follows
// the enum rules: status tones for a status field, category hue for a tag
// taxonomy, field hue for a plain enum.
export function arrayValueChip(
  schema: FactDefinition,
  item: FactValueType
): { label: string; tone: EnumToneStyles } | null {
  if (item.entity || item.document) return null;
  const scalar = item.scalar;
  if (scalar === null || scalar === undefined) return null;
  let label: string;
  let toneKey: string;
  if (Array.isArray(scalar)) {
    label = scalar.map(String).join(" > ");
    toneKey = String(scalar[scalar.length - 1] ?? "");
  } else if (typeof scalar === "string") {
    label =
      schema.scalar_type === "date"
        ? formatDateScalar(scalar, schema.scalar_unit)
        : (schema.enum_labels?.[scalar] ??
          (schema.enum_labels && scalar !== ""
            ? humanizeEnumKey(scalar)
            : scalar));
    toneKey = scalar;
  } else {
    return null;
  }
  // The one tone rule every surface shares (design/factValueTone, #12021):
  // a key outside a flat enum's declared vocabulary takes the none tone —
  // grey states the classification gap honestly — while a hierarchical
  // value is in-vocabulary by construction.
  return { label, tone: factValueTone(schema, toneKey) };
}

export function arrayChipStyle(tone: EnumToneStyles): React.CSSProperties {
  return {
    fontFamily: "var(--fp-font-ui)",
    fontSize: "var(--fp-t-fine)",
    fontWeight: "var(--fp-w-strong)" as unknown as number,
    letterSpacing: "var(--fp-track-body)",
    lineHeight: "var(--fp-lh-body)",
    padding: "2px var(--fp-gap-tight)",
    borderRadius: "var(--fp-chip-r)",
    border: `1px solid ${tone.state ? tone.borderColor : "transparent"}`,
    backgroundColor: tone.backgroundColor,
    color: tone.textColor,
  };
}

const FactRenderer = ({
  fact,
  singleLine = false,
  onEntityClick,
  trailing = null,
  addressMap,
}: {
  fact: ScalarFact | ArrayFact;
  singleLine?: boolean;
  onEntityClick?: (entity: any) => void;
  /**
   * A small mark that belongs to the value (the AI provenance ✦, #12035),
   * drawn on the same line as the value's last item. A list value is a
   * stack of chips, so a mark placed after the renderer lands on a line of
   * its own under the stack; this puts it beside the last chip instead.
   * A mapped address (below) reads it too, to keep the marks on the address
   * line rather than under the map. Every other single value is inline
   * text and the caller appends to it directly.
   */
  trailing?: React.ReactNode;
  /**
   * The record page's address map (#13870): the fact's mapped address
   * (`mappedAddressOf`) and the map's height, drawn under the address text.
   * Only the Facts tab row and the Overview rail pass it; every other
   * surface renders an address as text alone.
   */
  addressMap?: { address: MappedAddress; height: number };
}) => {
  if ("array" in fact) {
    // Rule 5: an absent value is "—", the same mark every other surface
    // uses. "No value" was a second grammar for the same idea one tab away.
    if (fact.array.length === 0) {
      return (
        <span className="select-none" style={{ color: "var(--fp-ink-4)" }}>
          —
        </span>
      );
    }

    if (singleLine) {
      return (
        <div
          className={`flex gap-1 items-center overflow-x-auto overflow-y-hidden ${singleLine ? "px-3 py-2" : ""} [&::-webkit-scrollbar]:hidden [-ms-overflow-style]:none [scrollbar-width:none]`}
        >
          {fact.array.map((item, index) => {
            const chip = arrayValueChip(fact, item);
            if (chip) {
              return (
                <span
                  key={index}
                  data-chip="tag"
                  className="inline-flex items-center whitespace-nowrap flex-shrink-0"
                  style={arrayChipStyle(chip.tone)}
                >
                  {chip.label}
                </span>
              );
            }
            return (
              <div
                key={index}
                className="inline-flex items-center whitespace-nowrap flex-shrink-0"
              >
                <FactValue
                  schema={fact}
                  value={item}
                  singleLine={singleLine}
                  onEntityClick={onEntityClick}
                />
              </div>
            );
          })}
        </div>
      );
    }
    // One value per line, in the same washed chip the grid draws (#11426,
    // round 8): one renderer for one field, on every surface. The vertical
    // rule that used to mark multi-value rows is gone — it added a second
    // rule direction to a hairline-only grammar and made single- and
    // multi-value rows structurally different. Non-scalar members (entity
    // references, documents) keep their own grammar unchipped. The chip's
    // BOX edge sits on the value column (#11426, CD ruling on Ankit's
    // 2026-08-29 gap doc item 8, reversing round 8's text-edge inset): a
    // washed, bordered chip has a drawn edge the eye reads directly, so
    // hanging it made a second left edge beside the status chips that were
    // never inset. One rule for every chip, needing no knowledge of chip
    // internals. No hover tooltip repeating the chip's own label — gaps-v4's
    // rule (#11426): a value never hover-restates its own text.
    const last = fact.array.length - 1;
    return (
      <div className="flex flex-col items-start gap-1.5">
        {fact.array.map((item, index) => {
          const chip = arrayValueChip(fact, item);
          const row = chip ? (
            <span
              data-chip="tag"
              className="inline-flex items-center max-w-full min-w-0"
              style={arrayChipStyle(chip.tone)}
            >
              <span className="truncate min-w-0">{chip.label}</span>
            </span>
          ) : (
            <div className="max-w-full min-w-0">
              <FactValue
                schema={fact}
                value={item}
                singleLine={singleLine}
                onEntityClick={onEntityClick}
              />
            </div>
          );
          if (index === last && trailing) {
            return (
              <div key={index} className="flex items-center max-w-full min-w-0">
                {row}
                {trailing}
              </div>
            );
          }
          return <React.Fragment key={index}>{row}</React.Fragment>;
        })}
      </div>
    );
  }

  if (singleLine) {
    return (
      <div className="px-3 py-2 h-full overflow-x-auto overflow-y-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style]:none [scrollbar-width:none]">
        <FactValue
          schema={fact}
          value={fact}
          singleLine={singleLine}
          onEntityClick={onEntityClick}
        />
      </div>
    );
  }

  if (addressMap) {
    // The map sits under the address, in the value's own column, so the
    // read view shows it where the editor does.
    return (
      <div className="w-full min-w-0">
        <span className="inline-flex items-center max-w-full min-w-0">
          <FactValue schema={fact} value={fact} onEntityClick={onEntityClick} />
          {trailing}
        </span>
        <AddressMap address={addressMap.address} height={addressMap.height} />
      </div>
    );
  }

  return (
    <FactValue
      schema={fact}
      value={fact}
      singleLine={singleLine}
      onEntityClick={onEntityClick}
    />
  );
};

export default FactRenderer;

// Simple value renderer that can be used anywhere
export interface SimpleValueRendererProps {
  value: any;
  scalarType: ScalarKind | null;
  scalarUnit?: string | null;
  // A numberlike number fact's range end and note (#10991): a range reads
  // "low – high" and the note sits after the numbers in muted ink.
  scalarEnd?: number | null;
  scalarNote?: string | null;
  isHierarchical?: boolean;
  // The hierarchical attribute's option list: a single-line value shows its
  // leaf, plus whatever ancestry keeps it distinct from the other paths in
  // the vocabulary (#12052).
  hierarchyOptions?: readonly unknown[] | null;
  // The other hierarchical values rendered in the same cell, as paths: a
  // leaf one of them also ends in keeps its ancestry too (#12052). Only an
  // array cell has siblings; a single value leaves this unset.
  hierarchySiblings?: readonly (readonly unknown[])[];
  isEnum?: boolean;
  // Token-derived tone for an enum value (#11426, round 5): judgement tones
  // for a status fact, the categorical ramp otherwise. Org hexColor never
  // reaches paint — callers resolve tones via `enumValueToneStyles`.
  toneStyles?: EnumToneStyles | null;
  truncate?: boolean;
  factName?: string;
  singleLine?: boolean;
  // The org declared this vocabulary a TAG taxonomy (it configured colors or
  // tag categories for its values — interaction types, document types).
  // In a table cell such a value renders as a .chip.tag wash; a plain
  // vocabulary (Domicile) renders as ink text (#11426, gaps-v2).
  tagVocabulary?: boolean;
  // Renders an enumerated value at metadata-line scale rather than panel scale:
  // sized to the 12px subheader it sits in and centring its own text, so a
  // boxed value neither makes the line taller than an unboxed one nor rides
  // above the label beside it. Set by the entity-card subheaders; the Facts
  // panel and every other caller keep the full-size box.
  compact?: boolean;
  // The CALLER owns the pill: it has already drawn the chip (border, tint,
  // dot) around this value and wants the label alone inside it. An enum
  // value then renders as plain text whatever tone it would otherwise
  // resolve — the one-layer rule (#12021). The earlier contract, handing
  // down a definition with some tone fields blanked, left the option list
  // in, and the "outside the vocabulary" route to a judgement tone drew a
  // second bordered, dotted pill inside the first.
  chipLabel?: boolean;
}

const getScalarIcon = (
  scalarType: ScalarKind | null,
  scalarUnit?: string | null
) => {
  if (scalarType === "boolean") return CheckCircle2;
  if (scalarType === "number") return Hash;
  if (scalarType === "date") return Calendar;
  if (scalarType === "address") return MapPin;
  return Sparkles;
};

export const SimpleValueRenderer: React.FC<SimpleValueRendererProps> = ({
  value,
  scalarType,
  scalarUnit,
  scalarEnd = null,
  scalarNote = null,
  isHierarchical,
  hierarchyOptions,
  hierarchySiblings,
  isEnum = false,
  toneStyles,
  truncate = false,
  factName,
  singleLine = false,
  tagVocabulary = false,
  compact = false,
  chipLabel = false,
}) => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return <span className="text-gray-400 italic">null</span>;
  }

  // Handle hierarchical values
  if (isHierarchical && Array.isArray(value)) {
    if (singleLine) {
      // The leaf, with the ancestry it needs to stay distinct from the
      // other paths in the vocabulary (#12052, design/hierarchicalValueLabel
      // — "Market Cap > All" beside "Duration > All" keeps its parent, a
      // lone "Venture" drops "Private Equity"). The whole path is the hover
      // text when the label left something out; a label that already is
      // the path gets none — a cell never hover-restates its own text
      // (#11426 gaps-v4).
      const fullPath = value.join(PATH_SEPARATOR);
      const label = hierarchicalValueLabel(value, {
        options: hierarchyOptions,
        siblings: hierarchySiblings,
      });
      const rendered = (
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
          <SimpleValueRenderer
            value={label}
            scalarType={scalarType}
            scalarUnit={scalarUnit}
            singleLine={singleLine}
          />
        </span>
      );
      return label === fullPath ? (
        rendered
      ) : (
        <Tooltip content={fullPath}>{rendered}</Tooltip>
      );
    }

    // Wraps at the path separators, never mid-token: in a narrow surface
    // (the overview rail) a nowrap path ran off the card edge, and four
    // paths sharing a prefix clipped into four copies of the same value
    // (#11426, keeper ruling A2 / rule 6). Each level is its own flex item,
    // so a break lands beside a chevron.
    return (
      <span className="inline-flex flex-wrap items-center gap-1">
        {value.map((level, index) => (
          <React.Fragment key={index}>
            {index > 0 && (
              <ChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
            )}
            <SimpleValueRenderer
              value={level}
              scalarType={scalarType}
              scalarUnit={scalarUnit}
              singleLine={singleLine}
            />
          </React.Fragment>
        ))}
      </span>
    );
  }

  const Icon = getScalarIcon(scalarType, scalarUnit);

  // Handle different scalar types
  if (scalarType === "boolean") {
    if (value === true) {
      return (
        <span className="inline-flex items-center gap-1 whitespace-nowrap text-green-700">
          <CheckCircle2 className="w-3 h-3" />
          Yes
        </span>
      );
    }
    // An explicit false mirrors "Yes": a red X plus the word, same size and
    // gap (#12358). This overrides CD ruling C6 (#11426 polish audit), which
    // made "No" quiet text with no icon; beside a checked "Yes" it read as a
    // missing value. An empty fact is kept out by the callers, which filter
    // empty scalars first and show "—" (FactValue, the table's cell renderers).
    if (value === false) {
      return (
        <span className="inline-flex items-center gap-1 whitespace-nowrap text-red-700">
          <XCircle className="w-3 h-3" />
          No
        </span>
      );
    }
    // Anything else (a non-boolean scalar on a boolean fact) keeps the quiet
    // text it always had: only a real false earns the X.
    return (
      <span className="whitespace-nowrap" style={{ color: "var(--fp-ink-3)" }}>
        No
      </span>
    );
  }

  if (scalarType === "number") {
    const main = formatNumberDisplay(value, scalarUnit);
    if (main === null) {
      // Not a number at all: the percent renderer flags it, the others show
      // the raw text, as they always have.
      return scalarUnit === "percent" ? (
        <span className="text-red-600 whitespace-nowrap">Invalid value</span>
      ) : (
        <span className="whitespace-nowrap">{String(value)}</span>
      );
    }
    // A numberlike fact (#10991): a range reads "low – high" and the note
    // that followed the numbers sits after them in muted ink. Nothing about
    // the value is hidden in a hover.
    const endText =
      typeof scalarEnd === "number"
        ? formatNumberDisplay(scalarEnd, scalarUnit)
        : null;
    const figure = endText === null ? main : `${main} – ${endText}`;
    // The note stands where it was written, before the figure, after it, or
    // both. The figure never breaks; the note is prose and wraps wherever the
    // surface is narrow (a single-line grid cell keeps it on the line).
    const { before, after } = splitNote(scalarNote);
    const noteStyle = { color: "var(--fp-ink-3)" };
    return (
      <span className={singleLine ? "whitespace-nowrap" : ""}>
        {before && (
          <span className="mr-1 font-normal" style={noteStyle}>
            {before}
          </span>
        )}
        <span className="whitespace-nowrap">{figure}</span>
        {after && (
          <span className="ml-1 font-normal" style={noteStyle}>
            {after}
          </span>
        )}
      </span>
    );
  }

  if (scalarType === "address") {
    let formatted = "";
    let addressValue: AddressValue | null = null;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      addressValue = value as AddressValue;
      formatted = addressValue.formatted ?? "";
    } else {
      formatted = String(value);
    }
    const displayValue = truncate ? truncateText(formatted, 60) : formatted;
    // The marker means "we checked and Google could not place it" (#13126).
    const warnIcon = isAddressUnplaceable(addressValue) ? (
      <Tooltip content={UNPLACEABLE_ADDRESS_TOOLTIP}>
        <AlertTriangle
          role="img"
          aria-label={UNPLACEABLE_ADDRESS_TOOLTIP}
          className="w-3 h-3 text-amber-500 flex-shrink-0"
        />
      </Tooltip>
    ) : null;
    if (singleLine) {
      // The warn icon's tooltip stays (it adds information); the value
      // itself never hover-repeats its own text (gaps-v4, #11426).
      return (
        <span className="inline-flex items-center gap-1 whitespace-nowrap truncate block w-full max-w-full text-slate-700">
          {warnIcon}
          <span
            className="truncate select-none"
            style={formatted ? undefined : { color: "var(--fp-ink-4)" }}
          >
            {formatted || "—"}
          </span>
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-slate-700">
        {warnIcon}
        {displayValue || (
          <span className="select-none" style={{ color: "var(--fp-ink-4)" }}>
            —
          </span>
        )}
      </span>
    );
  }

  if (scalarType === "date") {
    return (
      <span className="whitespace-nowrap">
        {formatDateScalar(value, scalarUnit)}
      </span>
    );
  }

  // Handle enum values
  if (isEnum) {
    const valueKey = String(value);
    if (chipLabel) {
      // Inside a pill the caller drew: the label, nothing else. A grid cell
      // (`singleLine`) is fixed-width and hides its overflow, so the label
      // truncates with an ellipsis there; an editor chip sits in a wrapping
      // row, so its label wraps inside the chip instead — with `nowrap` and
      // no bound it ran past the panel edge, remove glyph and all (#12027).
      return (
        <span
          className={singleLine ? "truncate min-w-0" : "min-w-0 break-words"}
        >
          {valueKey}
        </span>
      );
    }
    // `h-4` matches the line box of the 12px subheader the compact box sits in,
    // so the box occupies exactly one line and no more; `items-center` plus
    // `leading-none` centre the text within it.
    //
    // `max-w-full` + `min-w-0` let it shrink instead of running past its row:
    // a flex item defaults to `min-width: auto`, which refuses to go below its
    // content, so a long value used to overrun the card and get sliced mid-word
    // with its own right border cut off. The inner span carries the truncation
    // so the ellipsis lands inside the border rather than on it.
    const compactBox =
      "inline-flex items-center h-4 px-1.5 rounded border text-[11px] leading-none max-w-full min-w-0";

    // The panel-scale box. `max-w-full` plus wrapping is what keeps a long
    // value inside its column: with `whitespace-nowrap` and no max width, an
    // "Accredited Investor (3c1)" chip measured 160px in a 320px rail and
    // made the RAIL a horizontal scroll container — rule 6, and the frontend
    // rule that a value too wide for its field wraps rather than scrolls.
    // `break-words` covers a single token longer than the column.
    const PANEL_ENUM_BOX =
      "inline-block px-2 py-1 rounded text-xs border font-medium max-w-full break-words";

    // No tooltip restating the value the box already shows — Lakshay's
    // gaps-v4 rule (#11426): a cell never hover-repeats its own text.
    // Truncated values are read by widening the column or opening the
    // record.
    const compactInner = <span className="truncate min-w-0">{valueKey}</span>;

    if (toneStyles) {
      // In a table cell (#11426, gaps-v2 — the mock's table is binding):
      // a JUDGEMENT value renders the mock's `.chip.st` pill — semantic
      // border, tint, dot — while a CATEGORICAL value renders as plain ink
      // text, exactly as the mock's Domicile column does. Fifty filled
      // category boxes would spend the colour channel restating what the
      // column already says; a status pill is the one judgement mark the
      // row carries. The tone stays token-derived; the org's raw hex never
      // reaches paint (round 5).
      if (singleLine) {
        if (toneStyles.state) {
          return (
            <span
              data-chip="status"
              className="inline-flex items-center whitespace-nowrap max-w-full min-w-0"
              style={{
                gap: "var(--fp-gap-hair)",
                fontFamily: "var(--fp-font-ui)",
                fontSize: "var(--fp-t-fine)",
                fontWeight: "var(--fp-w-strong)" as unknown as number,
                letterSpacing: "var(--fp-track-body)",
                lineHeight: "var(--fp-lh-body)",
                padding: "2px var(--fp-gap-tight)",
                borderRadius: "var(--fp-chip-r)",
                border: `1px solid ${toneStyles.borderColor}`,
                backgroundColor: toneStyles.backgroundColor,
                color: toneStyles.textColor,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: "var(--fp-dot)",
                  height: "var(--fp-dot)",
                  borderRadius: "var(--fp-dot)",
                  backgroundColor: "currentColor",
                  flex: "none",
                }}
              />
              <span className="truncate min-w-0">{valueKey}</span>
            </span>
          );
        }
        if (tagVocabulary) {
          // The org declared this vocabulary a tag taxonomy (interaction
          // types, document types): the value renders as the mock's
          // borderless `.chip.tag` wash. A plain vocabulary stays ink text.
          return (
            <span
              data-chip="tag"
              className="inline-flex items-center whitespace-nowrap max-w-full min-w-0"
              style={{
                fontFamily: "var(--fp-font-ui)",
                fontSize: "var(--fp-t-fine)",
                fontWeight: "var(--fp-w-strong)" as unknown as number,
                letterSpacing: "var(--fp-track-body)",
                lineHeight: "var(--fp-lh-body)",
                padding: "2px var(--fp-gap-tight)",
                borderRadius: "var(--fp-chip-r)",
                border: "1px solid transparent",
                backgroundColor: toneStyles.backgroundColor,
                color: toneStyles.textColor,
              }}
            >
              <span className="truncate min-w-0">{valueKey}</span>
            </span>
          );
        }
        return <span className="whitespace-nowrap truncate">{valueKey}</span>;
      }
      return (
        <span
          // The same chip the grid marks, so a surface that aligns "every
          // chip" (the Key facts rail, #11426 item 8) can find it.
          data-chip={toneStyles.state ? "status" : "tag"}
          className={compact ? compactBox : PANEL_ENUM_BOX}
          style={{
            backgroundColor: toneStyles.backgroundColor,
            color: toneStyles.textColor,
            borderColor: toneStyles.borderColor,
          }}
        >
          {compact ? compactInner : valueKey}
        </span>
      );
    }

    // When singleLine is true (table cells), render without box styling
    if (singleLine) {
      return <span className="whitespace-nowrap">{valueKey}</span>;
    }

    // Default enum styling (no color)
    return (
      <span
        className={
          compact
            ? `${compactBox} bg-gray-100 text-gray-700 border-gray-300`
            : `${PANEL_ENUM_BOX} bg-gray-100 text-gray-800 border-gray-300`
        }
      >
        {compact ? compactInner : valueKey}
      </span>
    );
  }

  if (scalarType === "string") {
    const fullValue = String(value);
    const displayValue = truncate ? truncateText(fullValue, 50) : fullValue;

    // Handle singleLine (table cells): use CSS truncation so text adapts to
    // column width. No hover tooltip with the full value — gaps-v4's rule
    // (#11426): a cell never hover-repeats its own text; long values are
    // read by widening the column or opening the record. The wrapper keeps
    // the max-w-full constraint the old tooltip wrapper carried (keeper
    // ruling C5) so the inner span still meets its truncation limit.
    //
    // inline-FLEX, not inline-block (CD ruling C12, #11426 polish audit):
    // an inline-block whose overflow is hidden takes its BOTTOM edge as its
    // baseline, so inside a baseline-aligned row ("Summary" on a board
    // card) the text rode a descender higher than its label. A flex item
    // with hidden overflow still exports its first line's baseline, so the
    // truncation survives and the row's items-baseline means what it says.
    if (singleLine) {
      return (
        <span className="inline-flex max-w-full">
          <span className="text-slate-700 whitespace-nowrap truncate min-w-0 max-w-full">
            {fullValue}
          </span>
        </span>
      );
    }

    // Non-singleLine contexts
    if (scalarUnit === "multiline") {
      return (
        <span className="text-slate-700 whitespace-pre-wrap break-words">
          <Linkify text={displayValue} />
        </span>
      );
    }

    return (
      <span className="text-slate-700">
        <Linkify text={displayValue} />
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-amber-700 whitespace-nowrap">
      <Icon className="w-3 h-3" />
      {String(value)}
    </span>
  );
};

export interface ScalarValueRendererProps {
  schema: FactDefinition;
  scalar: any;
  // The range end and note of a numberlike number fact (#10991).
  scalarEnd?: number | null;
  scalarNote?: string | null;
  // The other values in the same array cell; see
  // SimpleValueRendererProps.hierarchySiblings.
  siblings?: readonly FactValueType[];
  singleLine?: boolean;
  // The caller owns the pill; render the label alone. See
  // SimpleValueRendererProps.chipLabel.
  chipLabel?: boolean;
}

export const ScalarValueRenderer: React.FC<ScalarValueRendererProps> = ({
  schema,
  scalar,
  scalarEnd = null,
  scalarNote = null,
  siblings,
  singleLine = false,
  chipLabel = false,
}) => {
  // For enum facts, prefer the enum_labels lookup so the rendered value is
  // the human-readable display label (e.g. "Capital Call") rather than the
  // raw stored key (e.g. "capital_call"). When a label-mapped enum has no
  // entry for THIS value -- e.g. a system/interaction document_type like
  // `notes_meetings_transcripts` that isn't in the org's configured
  // document_types -- humanize the key as a last resort so the cell never
  // shows the raw snake_case key. Falls through to scalar otherwise.
  const isLabelledEnum = schema.enum_labels != null;
  const displayValue =
    typeof scalar === "string" && schema.enum_labels?.[scalar]
      ? schema.enum_labels[scalar]
      : typeof scalar === "string" && isLabelledEnum && scalar !== ""
        ? humanizeEnumKey(scalar)
        : scalar;
  // The tone derives from the RAW stored key (status_semantics and the
  // vocabulary are keyed by it), not from the display label, by the one rule
  // every surface shares (design/factValueTone). Inside a pill the caller
  // drew there is no tone to resolve: the label is all that renders.
  const toneStyles =
    !chipLabel && typeof scalar === "string" && Boolean(schema.scalar_values)
      ? factValueTone(schema, scalar)
      : null;
  return (
    <SimpleValueRenderer
      value={displayValue}
      scalarType={schema.scalar_type}
      scalarUnit={schema.scalar_unit}
      scalarEnd={scalarEnd}
      scalarNote={scalarNote}
      isHierarchical={schema.is_hierarchical}
      hierarchyOptions={schema.scalar_values}
      hierarchySiblings={(siblings ?? []).flatMap((sibling) =>
        Array.isArray(sibling.scalar) ? [sibling.scalar] : []
      )}
      isEnum={Boolean(schema.scalar_values)}
      toneStyles={toneStyles}
      truncate={false}
      factName={schema.name}
      singleLine={singleLine}
      chipLabel={chipLabel}
      // A vocabulary the org gave colors or tag categories is a tag
      // taxonomy; its single values chip in table cells (#11426, gaps-v2)
      // — provided the vocabulary also earns the chip SHAPE: closed and
      // short (hard ceiling 12), a hierarchy (gaps-v6 item 11), or a
      // vocabulary the org configured colours for, which earns the chip
      // at any size (#12143). Only an uncoloured Domicile-sized list
      // stays plain ink.
      tagVocabulary={
        isValueHuedVocabulary(schema) &&
        earnsChip({
          is_enum: Boolean(schema.scalar_values),
          is_hierarchical: schema.is_hierarchical,
          scalar_values: schema.scalar_values,
          status_states: schema.status_states,
          enum_colors: schema.enum_colors,
          category_names: schema.category_names,
        })
      }
    />
  );
};

interface FactValueProps {
  schema: FactDefinition;
  value: FactValueType;
  singleLine?: boolean;
  onEntityClick?: (entity: any) => void;
}

// Helper function to check if a scalar value is effectively empty. Shared with
// the backreference rows (`linkCarriesValue`, #14370), so both sides agree on
// what counts as no value.
export const isScalarEmpty = (
  scalar: any,
  scalarType: ScalarKind | null
): boolean => {
  if (scalar === null || scalar === undefined) {
    return true;
  }
  // For string types, check if trimmed value is empty
  if (scalarType === "string" && typeof scalar === "string") {
    return scalar.trim() === "";
  }
  return false;
};

export const FactValue: React.FC<FactValueProps> = ({
  schema,
  value,
  singleLine = false,
  onEntityClick,
}) => {
  const isEntityOnly =
    schema.entity_types &&
    schema.entity_types.length > 0 &&
    !schema.scalar_type;
  const isScalarOnly =
    schema.scalar_type &&
    (!schema.entity_types || schema.entity_types.length === 0);
  const isComposite =
    schema.scalar_type && schema.entity_types && schema.entity_types.length > 0;

  // Entity-only case
  if (isEntityOnly) {
    if (value.entity) {
      if (singleLine) {
        return (
          <span className="whitespace-nowrap">
            <InlineEntityCard
              entity={value.entity}
              onEntityClick={onEntityClick}
            />
          </span>
        );
      }
      return (
        <InlineEntityCard entity={value.entity} onEntityClick={onEntityClick} />
      );
    }
    // Entity-only fact with no entity - show "No value"
    return (
      <span className="select-none" style={{ color: "var(--fp-ink-4)" }}>
        —
      </span>
    );
  }

  // Composite case (both scalar and entity)
  if (isComposite) {
    const hasScalar =
      value.scalar !== null &&
      value.scalar !== undefined &&
      !isScalarEmpty(value.scalar, schema.scalar_type);
    const hasEntity = value.entity !== null && value.entity !== undefined;

    // If both are empty, show "No value"
    if (!hasScalar && !hasEntity) {
      return (
        <span className="select-none" style={{ color: "var(--fp-ink-4)" }}>
          —
        </span>
      );
    }

    if (singleLine) {
      return (
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
          {hasScalar && (
            <ScalarValueRenderer
              schema={schema}
              scalar={value.scalar}
              scalarEnd={value.scalar_end}
              scalarNote={value.scalar_note}
              singleLine={singleLine}
            />
          )}
          {hasEntity && value.entity && (
            <InlineEntityCard
              entity={value.entity}
              onEntityClick={onEntityClick}
            />
          )}
        </span>
      );
    }
    return (
      <span className="inline-flex items-baseline flex-wrap">
        {hasScalar && (
          <ScalarValueRenderer
            schema={schema}
            scalar={value.scalar}
            scalarEnd={value.scalar_end}
            scalarNote={value.scalar_note}
            singleLine={singleLine}
          />
        )}
        {hasScalar && hasEntity && value.entity && (
          <span className="text-gray-400 mr-1">,</span>
        )}
        {hasEntity && value.entity && (
          <InlineEntityCard
            entity={value.entity}
            onEntityClick={onEntityClick}
            hideIcon
          />
        )}
      </span>
    );
  }

  // Scalar-only case
  if (isScalarOnly) {
    if (
      value.scalar !== null &&
      value.scalar !== undefined &&
      !isScalarEmpty(value.scalar, schema.scalar_type)
    ) {
      return (
        <ScalarValueRenderer
          schema={schema}
          scalar={value.scalar}
          scalarEnd={value.scalar_end}
          scalarNote={value.scalar_note}
          singleLine={singleLine}
        />
      );
    }
    // Scalar-only fact with no scalar value - show "No value"
    return (
      <span className="select-none" style={{ color: "var(--fp-ink-4)" }}>
        —
      </span>
    );
  }

  // Fallback for unsupported or null values
  if (
    (value.scalar === null ||
      value.scalar === undefined ||
      isScalarEmpty(value.scalar, schema.scalar_type)) &&
    !value.entity
  ) {
    return (
      <span className="select-none" style={{ color: "var(--fp-ink-4)" }}>
        —
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-red-600 whitespace-nowrap">
      <XCircle className="w-3 h-3" />
      Unsupported value type
    </span>
  );
};
